import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";

import EachHorizontalLine from "./wrapper/EachHorizontalLine";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class HorizontalLine extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleDrawHorizontalLine = this.handleDrawHorizontalLine.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDragHorizontalLine = this.handleDragHorizontalLine.bind(this);
    this.handleDragHorizontalLineComplete =
      this.handleDragHorizontalLineComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState =
      isHoverForInteractiveType("horizontalLines").bind(this);

    this.state = {};
    this.nodes = [];
  }

  handleDragHorizontalLine(index, newValues) {
    this.setState({
      override: {
        index,
        ...newValues,
      },
    });
  }

  handleDragHorizontalLineComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { horizontalLines } = this.props;
      const newHorizontalLines = horizontalLines.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              yValue: override.yValue,
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newHorizontalLines, moreProps);
        },
      );
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { enabled } = this.props;
    if (!enabled) return;

    const { current } = this.state;

    if (isNotDefined(current) || isNotDefined(current.yValue)) {
      this.setState(
        {
          current: {
            yValue: xyValue[1],
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleDrawHorizontalLine(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.yValue)) {
      this.setState({
        current: {
          yValue: xyValue[1],
        },
      });
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { enabled, horizontalLines, appearance, chartSectionId } = this.props;
    if (!enabled) return;

    // Issue #330: `activeInteractive` is global per chart, so every panel's
    // InteractiveLayer mounts an enabled HorizontalLine and the click fires
    // `onClick` on all of them. Without this guard, a single draw committed a
    // duplicate line into the Volume (and every other) sub-panel. Only the
    // panel the cursor is actually over should receive the line. Mirrors the
    // guard already present in VerticalLine.handleEnd.
    const morePropsSectionId = moreProps?.chartConfig?.id;
    const currentCharts = moreProps?.currentCharts;
    const isWrongPanel =
      (isDefined(chartSectionId) &&
        isDefined(morePropsSectionId) &&
        String(chartSectionId) !== String(morePropsSectionId)) ||
      (Array.isArray(currentCharts) &&
        isDefined(chartSectionId) &&
        !currentCharts.some((id) => String(id) === String(chartSectionId)));
    if (isWrongPanel) {
      // Clear any temp line left over from handleStart so no ghost remains
      // in the skipped panel.
      if (isDefined(this.state.current)) {
        this.setState({ current: null });
      }
      return;
    }

    const yValue = xyValue[1];

    const newHorizontalLines = [
      ...horizontalLines.map((d) => ({ ...d, selected: false })),
      {
        yValue,
        selected: true,
        appearance,
      },
    ];

    this.setState(
      {
        current: null,
      },
      () => {
        this.props.onComplete(newHorizontalLines, moreProps, e);
      },
    );
  }

  render() {
    const { appearance, horizontalLines, hoverText } = this.props;
    const { enabled, snap, shouldDisableSnap, snapTo } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionstrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { current, override } = this.state;

    const tempLine = isDefined(current) ? (
      <EachHorizontalLine
        interactive={false}
        yValue={current.yValue}
        stroke={
          appearance.stroke && typeof appearance.stroke === "object"
            ? appearance.stroke.color || "#000000"
            : appearance.stroke || "#000000"
        }
        strokeWidth={
          appearance.stroke && typeof appearance.stroke === "object"
            ? appearance.stroke.width || 1
            : appearance.strokeWidth || 1
        }
        strokeOpacity={
          appearance.stroke && typeof appearance.stroke === "object"
            ? appearance.stroke.opacity !== undefined
              ? appearance.stroke.opacity
              : 1
            : appearance.strokeOpacity !== undefined
              ? appearance.strokeOpacity
              : 1
        }
        strokeDasharray={
          appearance.stroke &&
          typeof appearance.stroke === "object" &&
          appearance.stroke.dash
            ? appearance.stroke.dash
            : "Solid"
        }
        displayFormat={this.props.displayFormat}
        coordinate={appearance.coordinate}
        showCoordinate={false}
      />
    ) : null;

    return (
      <g>
        {horizontalLines.map((each, idx) => {
          let eachAppearance = appearance;
          if (isDefined(each.appearance)) {
            eachAppearance = { ...appearance };
            if (
              each.appearance.stroke &&
              typeof each.appearance.stroke === "object"
            ) {
              eachAppearance.stroke = {
                ...(appearance.stroke || {}),
                ...(each.appearance.stroke || {}),
              };
            } else if (each.appearance.stroke) {
              eachAppearance.stroke = each.appearance.stroke;
            }
          }

          const hoverTextWithDefault = {
            ...HorizontalLine.defaultProps.hoverText,
            ...hoverText,
          };

          let coordinateAppearance =
            eachAppearance.coordinate || appearance.coordinate || {};
          if (
            isDefined(each.appearance?.coordinate) &&
            typeof each.appearance.coordinate === "object"
          ) {
            coordinateAppearance = {
              ...(appearance.coordinate || {}),
              ...each.appearance.coordinate,
            };
          }

          const {
            displayFormat: coordinateDisplayFormat,
            show: coordinateShow,
            ...restCoordinateAppearance
          } = coordinateAppearance || {};

          const showCoordinate = coordinateShow !== false;
          const displayFormatFn =
            coordinateDisplayFormat || this.props.displayFormat;

          return (
            <EachHorizontalLine
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              selected={each.selected}
              yValue={getValueFromOverride(
                override,
                idx,
                "yValue",
                each.yValue,
              )}
              stroke={
                eachAppearance.stroke &&
                typeof eachAppearance.stroke === "object"
                  ? eachAppearance.stroke.color || "#000000"
                  : eachAppearance.stroke || "#000000"
              }
              strokeWidth={
                eachAppearance.stroke &&
                typeof eachAppearance.stroke === "object"
                  ? eachAppearance.stroke.width || 1
                  : eachAppearance.strokeWidth || 1
              }
              strokeOpacity={
                eachAppearance.stroke &&
                typeof eachAppearance.stroke === "object"
                  ? eachAppearance.stroke.opacity !== undefined
                    ? eachAppearance.stroke.opacity
                    : 1
                  : eachAppearance.strokeOpacity !== undefined
                    ? eachAppearance.strokeOpacity
                    : 1
              }
              strokeDasharray={
                eachAppearance.stroke &&
                typeof eachAppearance.stroke === "object" &&
                eachAppearance.stroke.dash
                  ? eachAppearance.stroke.dash
                  : "Solid"
              }
              onDrag={this.handleDragHorizontalLine}
              onDragComplete={this.handleDragHorizontalLineComplete}
              hoverText={hoverTextWithDefault}
              displayFormat={displayFormatFn}
              coordinate={restCoordinateAppearance}
              showCoordinate={showCoordinate}
            />
          );
        })}
        {tempLine}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onMouseMove={this.handleDrawHorizontalLine}
          onClick={this.handleEnd}
          data-evo-id="interactive-horizontal-line"
        />
      </g>
    );
  }
}

HorizontalLine.propTypes = {
  enabled: PropTypes.bool.isRequired,
  onStart: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  snap: PropTypes.bool,
  snapTo: PropTypes.func,
  shouldDisableSnap: PropTypes.func.isRequired,
  displayFormat: PropTypes.func,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionstrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  hoverText: PropTypes.object,

  horizontalLines: PropTypes.array.isRequired,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
    ]),
    coordinate: PropTypes.shape({
      fill: PropTypes.string,
      opacity: PropTypes.number,
      textFill: PropTypes.string,
      fontFamily: PropTypes.string,
      fontSize: PropTypes.number,
      fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      stroke: PropTypes.string,
      strokeOpacity: PropTypes.number,
      strokeWidth: PropTypes.number,
      lineOpacity: PropTypes.number,
      lineStroke: PropTypes.string,
      rectWidth: PropTypes.number,
      rectHeight: PropTypes.number,
      arrowWidth: PropTypes.number,
      dx: PropTypes.number,
      orient: PropTypes.oneOf(["left", "right"]),
      at: PropTypes.oneOf(["left", "right"]),
    }),
  }).isRequired,
  isLastSection: PropTypes.bool,
  chartSectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

HorizontalLine.defaultProps = {
  onStart: noop,
  onComplete: noop,
  onSelect: noop,
  shouldDisableSnap: () => true,
  snap: false,

  currentPositionStroke: "#000000",
  currentPositionstrokeOpacity: 1,
  currentPositionStrokeWidth: 3,
  currentPositionRadius: 4,
  displayFormat: (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(2) : String(value);
  },

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },

  horizontalLines: [],

  appearance: {
    stroke: {
      color: "#2196F3",
      width: 2,
      opacity: 1,
      dash: "Solid",
    },
  },
  isLastSection: true,
};

export default HorizontalLine;
